import { type AnchorHTMLAttributes, useCallback, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useResizeObserver } from "@reactuses/core";
import { useDates } from "../../../dates/DatesContext";
import { generateUrl } from "../../../dates/utils";
import { CustomTriggerTooltip } from "./Tooltip";
import { css } from "../../../../styled-system/css";
import { linkOptionsFromHref } from "../../../utils/searchParams";

interface Props extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  /** App URL, optionally with its own query string; the current date range is added to it. */
  to: string;
}

const TitleLink = ({ to, title, ...rest }: Props) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useRef<HTMLAnchorElement>(null);

  const dates = useDates();
  const url = generateUrl(to, dates);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useResizeObserver(ref, checkTruncation);

  return (
    <CustomTriggerTooltip toolTip={isTruncated ? title : undefined} placement="top">
      <Link
        {...rest}
        {...linkOptionsFromHref(url)}
        ref={ref}
        className={css({
          cursor: "pointer",
          textDecoration: "none",
          margin: 0,
          padding: 0,
          fontSize: "1.5rem",
          fontWeight: 500,
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
          display: "block",
        })}
      >
        {title}
      </Link>
    </CustomTriggerTooltip>
  );
};

export default TitleLink;
